import { User } from "components/User";
import { toast } from "react-toastify";
import { FullScreenLoader, PageTitle } from "components";
import { useEffect, useMemo, useState } from "react";
import { useGetUserQuery, usePostUserMutation, useUpdateUserMutation } from "redux/api/userApi";
import { useGetAllCompaniesQuery } from "redux/api/companyApi";
import { ISelectOption } from "Interfaces/Utils";
import { ICompany } from "Models/Company";
import { useIntl } from "react-intl";

// Static vars

const USER_PAGE_URL = "/users";

/**
 * Component to render user creation form view
 */

const UserNewPage = () => {
  const { formatMessage } = useIntl();
  const PAGE_TITLE = formatMessage({ id: "user.new" });
  const RETURN_LABEL = formatMessage({ id: "user.back" });
  const CREATION_SUCCESS_LABEL = formatMessage({ id: "user.createdSuccessfuly" });
  const UPDATE_SUCCESS_LABEL = formatMessage({ id: "user.updatedSuccessfuly" });

  // Hook to create wizzard effect

  const [userId, setUserId] = useState<string | undefined>();

  const isEditing: boolean = useMemo(() => Boolean(userId), [userId]);

  // API calls to get, create and update user

  const { data: currentUser, isLoading: isGetUserLoading } = useGetUserQuery(String(userId), { skip: !userId });
  const [updateUser, { isSuccess: isUpdatedUserSuccess, error: updateApiError }] = useUpdateUserMutation();
  const [createUser, { data: postedUserData, isSuccess: isPostedUserSuccess, error: postApiError }] = usePostUserMutation();
  const { data: companies, isLoading: isCompaniesLoading } = useGetAllCompaniesQuery();


  // User creation success management

  useEffect(() => {
    if (isPostedUserSuccess && postedUserData) {
      toast.success(CREATION_SUCCESS_LABEL);
      setUserId(postedUserData._id);
    }
  }, [isPostedUserSuccess, postedUserData]);

  // User update management

  useEffect(() => {
    if (isUpdatedUserSuccess) {
      toast.success(UPDATE_SUCCESS_LABEL);
    }
  }, [isUpdatedUserSuccess]);

  // Function to call creation endpoint

  const postUserHandler = (formData: FormData) => {
    createUser(formData);
  };

  // Function to call edition endpoint

  const updateUserHandler = (formData: FormData) => {
    updateUser({
      userId: String(userId),
      updatedUser: formData,
    });
  };

  const groupSelectOptions: ISelectOption[] = useMemo(() => {

    if (!companies||!companies) return [];

    return companies.map((company: ICompany) => ({
      label: company.name,
      value: company._id,
    }));
    
  }, [companies])
  
  if (isGetUserLoading||isCompaniesLoading) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <PageTitle
        pageTitle={PAGE_TITLE}
        returnPage={USER_PAGE_URL}
        returnLabel={RETURN_LABEL}
      />
      <User.Form
        currentUser={currentUser}
        companies={groupSelectOptions}
        createApiError={postApiError}
        editApiError={updateApiError}
        onSubmit={isEditing ? updateUserHandler : postUserHandler}
      />
    </>
  );
};

export default UserNewPage;
